import {
  Component,
  OnInit,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ViewContainerRef,
  createNgModule,
  Injector,
  NgModuleRef,
  ComponentRef,
  ElementRef, OnDestroy, AfterViewInit,
} from "@angular/core";
import Map from "ol/Map";
import {FullScreen, defaults as defaultControls, ZoomSlider} from "ol/control.js";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS.js";
import { GeoJSON, MVT } from "ol/format";
import OlTileLayer from "ol/layer/Tile";
import TileWMS from "ol/source/TileWMS";
import OlView from "ol/View";
import TileArcGISRest from "ol/source/TileArcGISRest";
import OScaleLine from "ol/control/ScaleLine";
import proj4 from "proj4";
import { register } from "ol/proj/proj4.js";
import OlImage from "ol/layer/Image";
import OlSourceWMS from "ol/source/ImageWMS";
import OlOSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import BingMaps from "ol/source/BingMaps";
import VectorSource from "ol/source/Vector";
import { Layer } from "ol/layer";
import olZoomToExtent from "ol/control/ZoomToExtent";
import TileState from "ol/TileState";
import Rotate from "ol/control/Rotate";
import DragRotate from "ol/interaction/DragRotate";
import PinchRotate from "ol/interaction/PinchRotate";
import MouseWheelZoom from "ol/interaction/MouseWheelZoom";
import TileGrid from "ol/tilegrid/TileGrid";
import { AppLoadService } from "../services/app-load.service";
import { SearchQueryService } from "../services/search-query.service";
import { unByKey } from 'ol/Observable';
import { DisplayService } from "../services/display.service";
import { LayerService } from "../services/layer.service";
import { PDFService } from "../services/pdf.service";
import { getRenderPixel } from "ol/render";
import { defaults as defaultInteractions, DragPan } from "ol/interaction.js";
import { PlokkService } from "../services/plokk.service";
import { PlokkAppBoardComponent } from "../plokk/plokk-app-board/plokk-app-board.component";
import { LayoutService } from "../services/layout.service";
import { EditFeatureService } from "./query-views/services/edit-feature.service";
import { FeatureService } from "../services/feature.service";
import { featureQueryViewPackage, ActionViewItemType, mapLayer, mapChange, UserAccess, AppText, LayerParams, mapAndMainActionViewGrid, FeatureDrawInfo, ToastPosition, Languages, LayerType, GeometryTypes } from "../interfaces/common";
import Collection from "ol/Collection";
import Control from "ol/control/Control";
import { Extent } from "ol/extent";
import { Source } from "ol/source";
import BaseLayer from "ol/layer/Base";
import LayerRenderer from "ol/renderer/Layer";
import { Coordinate } from "ol/coordinate";
import { EventsKey } from "ol/events";
import { Feature, MapBrowserEvent } from "ol";
import VectorImageLayer from "ol/layer/VectorImage";
import { UserService } from "../services/user.service";
import { skip, take } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { GeoJSONFeatureCollection } from "ol/format/GeoJSON";
import { Geometry } from "ol/geom";
import WebGLVectorLayer from "ol/layer/WebGLVector";
import VectorTileSource from 'ol/source/VectorTile';
import VectorTileLayer from "ol/layer/VectorTile";
import VectorLayer from "ol/layer/Vector";
import { MainActionViewService } from "../services/main-action-view.service";
//import OLCesium from 'olcs';
//import LazyLoader from "olcs/src/contrib/LazyLoader.js";
import type OLCesium from 'olcs';

//Some defult settings below. Will be overwitten if config defines different values for these.
let map_epsg = "3857";
let mapProjection = "".concat("EPSG:", map_epsg);
let proj4_epsg: { [key: string]: string } = {
  3057: "+proj=lcc +lat_1=64.25 +lat_2=65.75 +lat_0=65 +lon_0=-19 +x_0=500000 +y_0=500000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  8088: "+proj=lcc +lat_1=64.25 +lat_2=65.75 +lat_0=65 +lon_0=-19 +x_0=2700000 +y_0=300000 +ellps=GRS80 +units=m +no_defs",
  3408: "+proj=laea +lat_0=90 +lon_0=0 +x_0=0 +y_0=0 +R=6371228 +units=m +no_defs +type=crs",
  3575: "+proj=laea +lat_0=90 +lon_0=10 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs +type=crs",
  8085: "+proj=longlat +ellps=GRS80 +no_defs +type=crs"
  //3857: "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs"
  //4326: "+proj=longlat +datum=WGS84 +no_defs +type=crs"
};

const mapTarget: string = "mapid";

let mapResolutions: number[] = [
  1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1,
];

const mapCenter: { [key: string]: number[] } = {
  "EPSG:3857": [-2100000, 9615900],
  "EPSG:8088": [2727697, 284629],
  "EPSG:3057": [500000, 500000],
  "EPSG:3408": [-901354, -2617723],
  "EPSG:3575": [-1342224, -2421435],
};
const mapZoomStart: { [key: string]: number } = {
  "EPSG:3857": 0,
  "EPSG:8088": 0,
  "EPSG:3057": 0,
  "EPSG:3408": 0,
  "EPSG:3575": 0,
};
let mapCurExtent: Extent;
/*
  Possible shit-mix for making lazyloading Cesium work.
  Because we are entirely lazy-loading Cesium from a CDN at runtime, your TypeScript compiler 
  doesn't know that a global variable named Cesium will magically appear in the browser later.
*/
declare var Cesium: any;

@Component({
  selector: "app-map",
  templateUrl: "./map.component.html",
  styleUrls: ["./map.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Output() updateMap = new EventEmitter();

  @Input() selText: AppText = {};
  @Input() mapLayers: mapLayer[] = [];
  @Input() settings: any;
  @Input() applicationName: string = "";
  @Input() applicationSettings: any;
  @Input() mapObject: Map = new Map();  // TODO initialize as null
  @Input() proxyLayers: number[] = [];
  @Input() isMobileView: boolean = false; //Initialized and updated in app.component
  @Input() lang: Languages = Languages.Icelandic;

  //initialMapLayers: mapLayer[] = [];
  mapView: OlView = new OlView();
  wmtsText: any = {};
  //Only 1 basemap can be checked at each time, currentBasemap contains the currently checked basemap
  queryData: any[] = [];
  //queryLayers: number[] = [];
  keyClickLatLon: any = null;
  clickEventOnMapCoordinates: any;
  center: Coordinate | undefined;
  showLayerLoadFailedNotification: boolean = false;
  layersThatFailed: any = undefined;
  //notificationText: object[] = [];
  notificationText: {
    text: string;
    style?: any;
  }[] = [];
  //startupNotificationText: object[] = [];
  startupNotificationText: {
    text: string;
    style?: any;
  }[] = [];
  startupNotification: boolean = false;
  allowCreate: boolean = false;
  allowFilter: boolean = false;
  filterColumn: string = "";
  specialConfigsLayerCheck: boolean = false;
  secondaryMapObject: Map = new Map();
  subAppSettings: any;

  /*
    Query views boolean active variables, so we can destroy the view when necessary.
    And their data value that comes in from the feature request, we will check what query view the incoming
    feature(a object) has and then assign it to the correct query View data container. When this happen,
    changeDetection will be called and the relevant query view will receive, process and display the data. 
  */
  featureDataFeature: featureQueryViewPackage | null = null;
  stationDataFeature: featureQueryViewPackage | null = null;
  externalDataFeature: featureQueryViewPackage | null = null;

  @ViewChild("moduleContainer", { read: ViewContainerRef })
  moduleContainer!: ViewContainerRef;
  moduleEntryComponent!: ComponentRef<any>;

  @ViewChild("mapAndSubappContainer") mapAndSubappContainer!: ElementRef;
  @ViewChild("mapElement") mapElement!: ElementRef; //Primary main map div

  /*
    Used so we can unlisten to click events on the secondaryMapObject when splitMap is closed.
    When splitMap is active, we listen to click events on secondaryMap in order to get and display
    new coordinates for the last click on map.
  */
  secondaryMapClickListenerKey: EventsKey | undefined;

  baseMapsMenuLeftOpen: boolean = false;
  mapsMenuLeftOpen: boolean = false;
  baseMapsMenuRightOpen: boolean = false;
  mapsMenuRightOpen: boolean = false;
  isIOS: boolean = false;
  isFullscreen: boolean = false;

  mapObject3D: OLCesium | null = null

  handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.isFullscreen) this.exitFullscreenForIos();
  }

  constructor(
    private appSettingsService: AppLoadService,
    public displayService: DisplayService,
    public layerService: LayerService,
    private PDF: PDFService,
    private searchQueryService: SearchQueryService,
    private plokkService: PlokkService,
    public editFeatureService: EditFeatureService,
    private userService: UserService,
    public layoutService: LayoutService,
    private featureService: FeatureService,
    private mainActionViewService: MainActionViewService,
    private http: HttpClient,
    private changeDetector: ChangeDetectorRef,
    private injector: Injector,
  ) {}

  ngOnInit(): void {
    if (this.applicationSettings === null || this.applicationSettings === undefined) return;

    this.isIOS = this.appSettingsService.getIsDeviceIOS();
    this.editFeatureService.filterText = this.userService.getUsername();
    this.wmtsText = this.appSettingsService.getWMTSText();
    /*
    this.displayService.interfaceToggle.subscribe(() => {
      // Trigger change detection to update the view when interface is toggled
      if (this.displayService.mapviewInterface.isSimple) {
        for (let i = 0; i < this.mapLayers.length; i++) {
          if (this.mapLayers[i].query) this.queryLayers.pop();
        }
      }
      else {
        for (let i = 0; i < this.initialMapLayers.length; i++) {
          if (this.mapLayers[i].query) this.queryLayers.push(i);
        }
      }
      this.displayService.setShowFeatureDataActive(false);
      this.displayService.setShowStationDataActive(false);
      this.changeDetector.detectChanges();
    });
    */

    //this.initialMapLayers = structuredClone(this.mapLayers);

    if ("option" in this.applicationSettings) {
      if ("map" in this.applicationSettings.option) {
        if ("epsg" in this.applicationSettings.option.map) {
          map_epsg = this.applicationSettings.option.map.epsg;
          this.displayService.setEPSG(map_epsg);
          this.searchQueryService.setEPSG(map_epsg);
          mapProjection = "".concat("EPSG:", map_epsg);
        }
        if ("proj4" in this.applicationSettings.option.map) proj4_epsg = this.applicationSettings.option.map.proj4;
        if ("center" in this.applicationSettings.option.map) mapCenter[mapProjection] = this.applicationSettings.option.map.center;
        if ("zoom" in this.applicationSettings.option.map) mapZoomStart[mapProjection] = this.applicationSettings.option.map.zoom;
        if ("resolutions" in this.applicationSettings.option.map) mapResolutions = this.applicationSettings.option.map.resolutions;
        if ("map_tools" in this.applicationSettings.option.map) {
          this.displayService.setMeasureVisable(this.applicationSettings.option.map.map_tools);
        }
      }
      if (this.applicationSettings?.option?.interface?.isSimple || (this.applicationSettings?.option?.interface && this.isMobileView)) {
        this.displayService.initiateSimpleInterfaceDisplay();
      }
    }

    this.displayService.setLayersQueryView(this.mapLayers);
    this.center = mapCenter[mapProjection]; // Initial center passed down to coordinates component, in same EPSG as the map

    for (const item in proj4_epsg) {
      proj4.defs("".concat("EPSG:", item), proj4_epsg[item]);
      register(proj4);
    }

    // const initialLoadedLayers: Layer[] = [];
    // const isSimpleInterface = this.applicationSettings?.option?.interface?.isSimple || (this.isMobileView && this.applicationSettings?.option?.interface);

    for (let i = 0; i < this.mapLayers.length; i++) {
      if (this.mapLayers[i].type === LayerType.GROUP) continue;

      // Need to mark authorized layers as 'hide' if user is not logged in or not authorized to see it
      const requiresAuthentication = this.mapLayers[i].option?.Authorization && !this.mapLayers[i].option?.Authorization?.username;
      if (requiresAuthentication && !this.mapLayers[i].onlyquery) {
        const isAuthorized: boolean = this.userService.isUserAuthorizedForLayer(this.mapLayers[i]);
        if (!isAuthorized) {
          this.mapLayers[i].hide = true;
          this.layerService.secondaryMapLayers[i].hide = true;
        }
      }

      if (this.mapLayers[i].attributions) {
        // Need to remove leading/trailing commas and spaces from attributions because comma is now set in the styles.css
        this.mapLayers[i].attributions = this.sanitizeAttribution(this.mapLayers[i].attributions);
      }
    }

    const initViewConfig: any = {
      projection: mapProjection,
      center: mapCenter[mapProjection],
      resolutions: mapResolutions,
      zoom: mapZoomStart[mapProjection],
    }
    
    const viewConfig = this.addConfigOptions(initViewConfig, this.applicationSettings.option?.map?.option?.view);
    const mapView = new OlView(viewConfig);
    this.mapView = mapView;
    
    const initMapConfig: any = {
      target: mapTarget,
      //layers: initialLoadedLayers,
      keyboardEventTarget: document,
      view: mapView,
      controls: defaultControls({ attribution: false }),  // Disable default attribution control because we add our own later if not disabled in config
      interactions: defaultInteractions(),
      /*  
      maxTilesLoading:
        HTTP/1.1 -> If the server returns requests in HTTP/1.1 then its most optimal do have maxTilesLoading at 6
        This is because in http1 we can only have 6 concurrent request at a time. If we had this number at f.ex 100
        then it will load at max 100 requests at once, problem is once there is mroe than 6 concurrent request then
        the tiles after the first 6 goes to the browsers network stack and waits there, then openLayers cant cancel these
        request if the user pans or zooms away. The requests on the browsers network stacks, openlayers cant cancel and
        it creates a gridlock where we are waiting for request to finish that are not currently in view and the user just
        stares at a blanck screen for a while.
        
        HTTP/2 -> Recomennded 32 to 100 for maxTilesLoading. HTTP/2 works differently it uses multiplexing, 1 connection
        can handle multiple concurrent requests. Could be useful to check what the servers "SETTINGS_MAX_CONCURRENT_STREAMS"
        or similar for what is the maximum concurrent number, maxTilesLoading should not be higher than that.
      */
      maxTilesLoading: 6
    }

    this.mapObject = new Map(
      this.addConfigOptions(initMapConfig, this.applicationSettings.option?.map)
    );

    //Lad layers checked from the start in config
    const startLayers: mapLayer[] = this.mapLayers.filter((ml: mapLayer) => ml.checked)
    this.mapChange3({layers: startLayers, newCheckStatus: true, isSecondaryMap: false})
    
    if (this.applicationSettings?.option?.map?.map_tools?.fullscreen && !this.isIOS) {
      this.mapObject.addControl(new FullScreen());
    }

    /* adding/removing/modifying openLayers Controls */
    this.mapObject.addControl(new ZoomSlider());
    const mapControls: Collection<Control> = this.mapObject.getControls();

    //Special loop to remove Rotate control -> Strange behaviour if I do this in the forEach loop beneath this one
    mapControls.forEach((control) => {
      if (control instanceof Rotate) mapControls.remove(control);
    });
    
    const scalePosition: OScaleLine = new OScaleLine({
      units: "metric",
      minWidth: 100,
    });
    this.mapObject.addControl(scalePosition);

    mapCurExtent = this.mapObject
      .getView()
      .calculateExtent(this.mapObject.getSize());
    if (this.displayService.getShowExtent()) {
      const zoomToExtent = new olZoomToExtent({
        extent: mapCurExtent,
      });
      this.mapObject.addControl(zoomToExtent);
    }

    /* adding/removing/modifying openLayers Interactions */
    const mapInteractions = this.mapObject.getInteractions().getArray();
    for (let i = 0; i < mapInteractions.length; ++i) {
      if (mapInteractions[i] instanceof DragRotate || mapInteractions[i] instanceof PinchRotate) {
        this.mapObject.removeInteraction(mapInteractions[i]);
      }
      if (mapInteractions[i] instanceof MouseWheelZoom) {
        const disableMouseWheelZoom: boolean = this.applicationSettings?.option?.map?.disableMouseWheelZoom ? false : true;
        mapInteractions[i].setActive(disableMouseWheelZoom);
      }
    }

    this.__setKeyclick();
    this.mapObject.render();

    this.displayService.setMapObject(this.mapObject);
    this.featureService.setMapObject(this.mapObject);
    this.editFeatureService.setMapObject(this.mapObject);
    this.displayService.setMapAttributionsActive(true)

    this.showLayerLoadFailedNotification = this.appSettingsService.didSomeLayerFail();

    if (this.showLayerLoadFailedNotification) {
      this.layersThatFailed = this.appSettingsService.getFailedLayers();
      this.notificationText = [
        { text: this.selText["warningWMTSLayerError"] },
        { text: this.layersThatFailed[this.lang], style: { fontWeight: "bold" } },
        { text: this.selText["warningWMTSLayerErrorOK"] }
      ];
    }

    document.getElementById("mapid")?.focus({ preventScroll: true });
    this.PDF.addMap(this.mapObject, this.mapLayers);

    const secondaryMap = document.getElementById("secondaryMap");
    if (secondaryMap) secondaryMap.style.display = "none";

    const splitMapSlider = document.getElementById("splitMapSlider");
    if (splitMapSlider) splitMapSlider.style.display = "none";

    const splitMapControlsContainer = document.getElementById("splitMapControlsContainer");
    if (splitMapControlsContainer) splitMapControlsContainer.style.display = "none";

    this.subAppSettings = this.plokkService.getAppSettings();
    this.updateMap.emit(this.mapObject);

    //URLMarker needs to be handled here instead of in app.component like the other URL-parameters because hte map/layer needs to be loaded
    const URLMarker: string | undefined = this.appSettingsService.getUrlParams().get("marker");
    if (URLMarker) this.handleURLMarker(URLMarker);

    this.featureService.initiateMapQueryListening(this.mapLayers, this.applicationName, this.applicationSettings)
    //All incoming features to show in query views wil come in here and only here
    this.featureService.getFeatureToQueryView().subscribe((fqvp: featureQueryViewPackage) => {
      //console.log("Received new featureQueryViewPackage from getFeatureToQueryView obs. It was:", fqvp)
      if (!fqvp) {
        console.warn("Supplied featureQueryViewPackage was not valid, it was: ")
        console.warn(fqvp)
        return
      }

      //SubApp defines divertFeatureRequest: true || false. If true then divert feature requests to the subApp and return early.
      //note 23.02.24: This was before inf feature.service. But we get a circular dependency injection error if its there, so its here now
      if (this.subAppSettings?.divertFeatureRequest) {
        this.plokkService.setNewGeoFeatures(fqvp);
        return;
      }
      
      //TODO: Check if both calls to detectChanges is necessary anymore....
      this.changeDetector.detectChanges();
      this.sendFeaturesToQueryView(fqvp);
      this.changeDetector.detectChanges();
    })

    const newMaplayersStatus$ = this.layerService.getNewMaplayersStatus()

    //All layer turning on/off comes trhough here
    newMaplayersStatus$.subscribe((mapChangeObj: mapChange) => {
      this.mapChange3(mapChangeObj);
    })

    this.displayService.splitMapActive$.pipe(skip(1)).subscribe((newSplitMapState: boolean) => {
      this.toggleSplitMap(newSplitMapState);
    })

    if (this?.applicationSettings?.option?.notification && this.selText.startup_notification) {
      this.localStorageNotification(this.selText.startup_notification);
    }

    /*
      Determine if this applications has editable features, if so then initalize and set up some listeners
      Note 26.03.24 maybe check different value than geoEditable later....
      Some of this should maybe be called in create.component?
    */
    if (this.applicationSettings?.option?.edit) {
      this.allowCreate = this.displayService.getAllowCreate();
      [this.allowFilter, this.filterColumn] = this.displayService.getFilterInfo();

      this.editFeatureService.handleEditableFeatures();

      //If defined nafberiField then we need to fetch nafnberar info from server(this is örnefnaritill app)
      const hasNafnberi: boolean = this.editFeatureService.hasNafnberiField();
      if (hasNafnberi) this.editFeatureService.getAndSetNafnberar()

      if (this.applicationSettings.option?.edit?.restrictedArea) {
        this.userService.getIsUserLoggedIn().subscribe((isLoggedIn: boolean | null) => {
          if (isLoggedIn && this.userService.userAccess >= UserAccess.Read) {
            this.editFeatureService.getAndSetAllAreas().subscribe();
            this.editFeatureService.getAndSetUserConfigAreas(true);
            this.displayService.clearLoginRequiredToast(); // Clear any login toast if user is now logged in
          }
          else if (this.applicationSettings.option?.edit?.showStartApplicationLoginMsg) {
            this.displayService.showLoginRequiredToast(this.selText.startApplicationLoginMsg);
          }
        });
      }
      if (!this.displayService.getIsTouchDevice()) {
        // Add scroll button panning to allow users to move map while drawing new feature
        this.addScrollbuttonInteraction();
      }
    }

    this.featureService.isMouseOverAFeature().subscribe((isMouseOverFeature: boolean) => {
      const mapElem: HTMLElement = this.mapObject.getTargetElement()
      mapElem.style.cursor = isMouseOverFeature ? "pointer" : ""
    })

   //this.startFeatHoverStyling()

   //const OL3DMap: OLCesium = new OLCesium({map: this.mapObject})
   //OL3DMap.setEnabled(false)
   //const imgLayers = OL3DMap.getCesiumScene().imageryLayers
   //setTimeout(() => {
    //this.toggle3DMap()
    //console.log("3D")
   //}, 10000)

   this.displayService.is3DModeActive$.subscribe((new3DActiveState: boolean) => {
    //console.log(new3DActiveState)
    this.toggle3DMap(new3DActiveState)
   })
  
  }
  /*
  startFeatHoverStyling() {
    let latestHoveredFeature: FeatureLike | null = null
    const hoveredOverFeatureCallback = (feat: FeatureLike | undefined): void => {
      //console.log("IM OVER or not: ", feat)
      if (feat) {
        const currentTickets: actionViewItem[] =this.mainActionViewService.actionViewItems()
        console.log(currentTickets)
        const chartTickets: actionViewItem[] = currentTickets.filter((avi) => avi.type === ActionViewItemType.chart)
        if (chartTickets.length > 0) {
          const hoverFeatID = feat.getId()
          console.log(hoverFeatID)
          const ticketFeatID = chartTickets[0]?.originalData?.geoJSONFeature?.features?.[0]?.id
          console.log(ticketFeatID)
          if (hoverFeatID === ticketFeatID) {
            (feat as Feature).setProperties({isHovered: true})
          }
        }
        return
      }

      if (!feat) {
        if (latestHoveredFeature) {
          (latestHoveredFeature as Feature).setProperties({isHovered: false})
          latestHoveredFeature = null
        }
        return
      }
      if (feat && latestHoveredFeature) {
        if (feat !== latestHoveredFeature) {
          (latestHoveredFeature as Feature).setProperties({isHovered: false})
        }
      }
      latestHoveredFeature = feat;
      (feat as Feature).setProperties({isHovered: true})

    }
    this.featureService.featureUnderMouse().subscribe(hoveredOverFeatureCallback)
  }
  */
  ngOnChanges(changes: SimpleChanges): void {
    const changeArray = Object.keys(changes); // Array of stringified names of the Inputs that changed
    if (changeArray.includes("settings") && changes.settings.firstChange == false) this.reloadMapview(changes);

    //This is here to update the notification in case the language is changed
    if (this.showLayerLoadFailedNotification) {
      this.notificationText = [
        { text: this.selText["warningWMTSLayerError"] },
        {
          text: this.layersThatFailed[this.lang],
          style: { fontWeight: "bold" },
        },
        { text: this.selText["warningWMTSLayerErrorOK"] },
      ];
    }

    if (changeArray.includes("proxyLayers")) {
      //console.log("IN MAP RECEIVED REQUEST FOR PROXYLAYERS:", this.proxyLayers)
      this.reloadLayersWithNewURL();
    }

    // Need to manually update inputs to lazy-loaded module/component if they change
    if (changeArray.includes("selText")) this.moduleEntryComponent?.setInput("selText", this.selText);
    if (changeArray.includes("isMobileView")) this.moduleEntryComponent?.setInput("isMobileView", this.isMobileView);

    this.PDF.addMap(this.mapObject, this.mapLayers);

    if (this.startupNotification && changes?.lang?.currentValue != changes?.lang?.previousValue) {
      this.localStorageNotification(this.selText.startup_notification);
    }

    // Need special config settings if user is logged into an editable mapview
    this.userService.getIsUserLoggedIn()
      .pipe(
        take(1)
      )
      .subscribe((isLoggedIn: boolean | null) => {
        if (isLoggedIn && this.applicationSettings.option?.edit && this.userService.userAccess > UserAccess.None) {
          if (!this.specialConfigsLayerCheck && (this.applicationSettings.option?.edit?.turnOnLayers || this.applicationSettings.option?.edit?.turnOffLayers)) {
            this.layerService.setCheckedStatusForSpecialConfigLayers(this.applicationSettings);
            this.specialConfigsLayerCheck = true;
          }
          this.displayService.enableSplitMap = false

          // Need to turn off query option for only query layers so they don't interfere with the editable layers
          if (this.applicationSettings?.option?.edit) {
            this.layerService.disableQueryLayers(this.applicationSettings.option.edit.disableQueryLayers);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.displayService.clearLoginRequiredToast();
    document.removeEventListener('keydown', this.handleKeyDown);
  }
  /**
   * Toggles on/off 3d mode.
   * If this is first time toggling on, lazy-loads in all required libraries.
   * @param newActive3DState boolean - says if 3d mode is on or not
   * @returns 
   */
  async toggle3DMap(newActive3DState: boolean) {
    //This is just to handle the very first emission from displayService.is3DModeActive$ because its a behaviourSubject - maybe refactor later
    if (!newActive3DState && !this.mapObject3D) return

    if (this.mapObject3D) {
      this.mapObject3D.setEnabled(newActive3DState)
      if (!newActive3DState) {
        this.mapView.setRotation(0)
      }
      return
    }
    try {
      // 1. DYNAMICALLY IMPORT OLCS
      const { default: OLCesium } = await import('olcs');
      const { default: LazyLoader } = await import('olcs/contrib/LazyLoader.js');
      // Pinning to a stable, recent Web version of CesiumJS
      const cesiumVersion: string = '1.110.0'; 
      const cesiumURL: string = `https://unpkg.com/cesium@${cesiumVersion}/Build/Cesium/Cesium.js`;
      
      // 1. Set the global Base URL *before* loading the script so Web Workers know where to go
      (window as any).CESIUM_BASE_URL = `https://unpkg.com/cesium@${cesiumVersion}/Build/Cesium/`;

      // 2. Initialize the dynamic script loader and await its completion
      const loader = new LazyLoader(cesiumURL);
    
      await loader.load(); // This injects the <script> tag dynamically and waits for it to download

      // 3. Dynamically inject the matching Cesium CSS into the document head
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://unpkg.com/cesium@${cesiumVersion}/Build/Cesium/Widgets/widgets.css`;
      document.head.appendChild(link);

      // 4. Instantiate the 3D globe using your active OpenLayers map instance
      this.mapObject3D = new OLCesium({ map: this.mapObject });
      //Get scene for furter operations
      const scene = this.mapObject3D.getCesiumScene();
      //Remove "Cesium ion" logo
      (scene as any).frameState.creditDisplay.container.style.display = 'none';

      //Load in custom .terrain files for height data
      /*
      const terrainNATT = await Cesium.CesiumTerrainProvider.fromUrl(
        'https://ftp.natt.is/public/marco/dem/cesium_terrain_2m/',
        {
          requestVertexNormals: true // Enable if the dataset includes terrain lighting/normals
        }
      );
      scene.terrainProvider = terrainNATT
      */
      //Load in hardcoded vopnafjörður data for now
      //const vopnURL: string = 'https://gis.natt.is/gisdata/public/vopnafjordur/tileset.json';
      //const vopnTileset3D = await (Cesium as any).Cesium3DTileset.fromUrl(vopnURL);
      //scene.primitives.add(vopnTileset3D);
      // Zoom the camera straight to it
      //scene.camera.flyTo(tileset3D.boundingVolume);

      //Load in hardcoded Lónsöræfi
      //const lonsURL: string = "https://gis.natt.is/gisdata/public/lonsoraefi/tileset.json"
      //const lonsTileset3D = await (Cesium as any).Cesium3DTileset.fromUrl(lonsURL);
      //scene.primitives.add(lonsTileset3D);
      //Load in OSM buildings
      //(Cesium as any).Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI0ZWRmY2RjNS04NDJkLTQyMDQtODJhZC01YjdlYzYzNjNkMjYiLCJpZCI6NDUzNTY0LCJpc3MiOiJodHRwczovL2FwaS5jZXNpdW0uY29tIiwiYXVkIjoidW5kZWZpbmVkX2RlZmF1bHQiLCJpYXQiOjE3ODM0NDE4ODJ9.ryg_OsGpTxewe8BuAvKbfl9oF7ypS8emsq99QXsxR10';
      //const buildingsTilesetZ = await Cesium.createOsmBuildingsAsync();
      //const scene = this.mapObject3D.getCesiumScene();
      //scene.primitives.add(buildingsTilesetZ);
      // 5. Turn on the 3D view
      this.mapObject3D.setEnabled(newActive3DState);
      
      console.log('Cesium 3D environment successfully lazy-loaded.');

    } catch (error) {
      console.error('Failed to lazy-load Cesium from the CDN:', error);
    }
  }

  /**
   * Takes in coordinates for a marker specified from the URL, creates a feature for it and draws it on the map.
   * Also pans the map to these coordinates
   * @param markerCoordinates string representation of coordinates in latLong(EPSG:4326) format. example: 64.3212565,-22.0655554
   */
  handleURLMarker(markerCoordinates: string): void {
    const [lat, long]: [string, string] = markerCoordinates.split(",") as [string, string]
    //console.log(`lat: ${lat} | long: ${long}`)
    if (!lat || !long) {
      console.warn(`Coordinates for marker were not valid. coordinates: lat: ${lat} | long: ${long}`);
      return;
    }
    const featToDraw: FeatureDrawInfo = {
      geometry: { coordinates: [+long, +lat], type: GeometryTypes.Point },
      featureCRS: "EPSG:4326"
    }
    
    setTimeout(() => {
      this.displayService.displayFeature(featToDraw, 0, "marker", {pan: true, draw: true, zoom: null});
    }, 500)

  }

  toggleBaseMapsMenuLeft(): void {
    if (!this.baseMapsMenuLeftOpen) this.mapsMenuLeftOpen = false;
    this.baseMapsMenuLeftOpen = !this.baseMapsMenuLeftOpen;
  }

  toggleMapsMenuLeft(): void {
    if (!this.mapsMenuLeftOpen) this.baseMapsMenuLeftOpen = false;
    this.mapsMenuLeftOpen = !this.mapsMenuLeftOpen;
  }

  toggleBaseMapsMenuRight(): void {
    if (!this.baseMapsMenuRightOpen) this.mapsMenuRightOpen = false;
    this.baseMapsMenuRightOpen = !this.baseMapsMenuRightOpen;
  }

  toggleMapsMenuRight(): void {
    if (!this.mapsMenuRightOpen) this.baseMapsMenuRightOpen = false;
    this.mapsMenuRightOpen = !this.mapsMenuRightOpen;
  }

  localStorageNotification(notificationText: string): void {
    this.startupNotification = false;
    notificationText = this.selText.startup_notification;
    const lang = this.selText.lang;
    const defaultDaysBetweenPromp = 365;
    const storedLastPopupConfirmation =
    Number(localStorage.getItem(lang === Languages.Icelandic ? "lastPopupConfirmation" : "lastPopupConfirmationEn"));
    const storedPopupMessage = lang === Languages.Icelandic ? localStorage.getItem("popupMessage"): localStorage.getItem("popupMessageEnglish") || ""; // Use empty string if null
    const daysBetweenPrompt = this?.applicationSettings?.option?.notification?.daysBetween || defaultDaysBetweenPromp;
    const xTimeAgo = Date.now() - daysBetweenPrompt * 24 * 60 * 60 * 1000; // in milliseconds

    const hasPopupConfirmationExpired = !storedLastPopupConfirmation || storedLastPopupConfirmation < xTimeAgo;
    const hasPopupMessageChanged = storedPopupMessage !== notificationText;

    if (hasPopupConfirmationExpired || hasPopupMessageChanged) {
      this.startupNotification = true;
      this.startupNotificationText = [
        {
          text: notificationText,
          style: { fontWeight: "bold" },
        },
      ];
      
      this.updateLocalStorageItem(lang, hasPopupMessageChanged, hasPopupConfirmationExpired, notificationText);
    }
  }

  updateLocalStorageItem(lang: string, hasMessageChanged: boolean, hasConfirmationChanged: boolean, value: string): void {
    const messageKey = lang === Languages.Icelandic ? "popupMessage" : "popupMessageEnglish";
    const confirmationKey = lang === Languages.Icelandic ? "lastPopupConfirmation" : "lastPopupConfirmationEn";

    if (hasMessageChanged) localStorage.setItem(messageKey, value);
    if (hasConfirmationChanged) localStorage.setItem(confirmationKey, Date.now().toString());
  }

  slideBegin(e: Event) {
    const updateWidth = (e: MouseEvent | TouchEvent): void => {
      //preventDefault() is for stopping the chance that the cursor selects text when resizing the window
      if (e instanceof MouseEvent) e.preventDefault()
      /*
        This is here such that the sliding calculations are correct. clientX and clientY are the whole viewport,
        but since for example we have a header that has some height, then we want when were sliding on y-axis to
        account for this so it looks correct. (In future need to look at/account for footer aswell... and maybe other stuff)
      */
      const rectOfMapSpace: DOMRect = this.mapAndSubappContainer.nativeElement.getBoundingClientRect()
      const newX: number = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX
      const newY: number = (e instanceof MouseEvent ? e.clientY : e.touches[0].clientY) - rectOfMapSpace.top

      if (newX < 0 || newY < 0) return
      window.innerWidth > 900 ? this.layoutService.updateMainActionViewWidth(newX) : this.layoutService.updateMainActionViewWidth((window.innerHeight - 44) - newY) 
    }
    window.addEventListener(e instanceof MouseEvent ? "mousemove" : "touchmove", updateWidth)

    const stopUpdateWidth = (e: MouseEvent | TouchEvent): void => {
      window.removeEventListener(e instanceof MouseEvent ? "mousemove" : "touchmove", updateWidth)
    }
    window.addEventListener(e instanceof MouseEvent ? "mouseup" : "touchend", stopUpdateWidth, {once: true})
  }

  toggleExpandedState(): void {
    if (this.layoutService.getCurrentmainActionViewWidth() > 0) this.layoutService.updateMainActionViewWidth(0);
    else this.layoutService.updateMainActionViewWidthToInitState();
  }

  ngAfterViewInit(): void {
    if (this.displayService.getIsSubAppActive()) {
      this.mapAndSubappContainer.nativeElement.style.setProperty("--mapSubappSpacing", this.layoutService.getMapToSubAppRatio());
      this.lazyLoadModule(this?.applicationSettings?.option?.subApp?.subAppName);
    }

    if (this.applicationSettings.option?.edit) this.setupZoomWatcher();

    this.layoutService.listenToMainActionViewActiveState();
    this.layoutService.mainActionViewAndMapGrid$.subscribe((newLayout: mapAndMainActionViewGrid) => {
      this.mapAndSubappContainer.nativeElement.style.setProperty("--mainActionViewAndMapColumns", newLayout.columnAreas);
      this.mapAndSubappContainer.nativeElement.style.setProperty("--mainActionViewAndMapColumnsSpacing", newLayout.columnsSpacing);
    })
    this.initSecondaryMap(); 
    //After changing to a CSS grid we need to update size of map here, else it might overflow. (Map is bigger than it should be...)
    this.mapObject.updateSize();
  }

  /**
    * Lazy loads an Angular module. Note: the full path of the module must be defined in "files" category in tsconfig.app.json.
    * so for plokk we have an entry there: "src/app/plokk/plokk.module.ts"
    * @param subAppName {string} - The name of the application
    *
  */
  lazyLoadModule(subAppName: string): void {
    const possibleApps: string[] = ["plokk"];

    if (!subAppName || !possibleApps.includes(subAppName)) {
      this.displayService.setSubAppActive(false);
      console.warn(
        "subApp was defined for this application, but subAppName was not valid or the subApp doesnt exist. subAppName was: " +
          subAppName
      );
      return;
    }
    //const yyy: string = `${subAppName}/${subAppName}`
    import(`../../app/${subAppName}/${subAppName}.module.ts`).then(
      (plokkModule: any) => {
        //`../plokk/plokk.module`
        //Create the module..
        const moduleRef: NgModuleRef<unknown> = createNgModule(
          plokkModule.PlokkModule,
          this.injector
        );
        this.moduleContainer.clear();
        //TODO: entry component needs to be decided by config
        //Dynamically create the entry-point component for the plokk app(whichs is PlokkAppBoardComponent) and attach to the DOM.
        this.moduleEntryComponent = this.moduleContainer.createComponent(
          PlokkAppBoardComponent,
          { ngModuleRef: moduleRef }
        );

        //If component depends on some inputs we can supply them with setInput
        this.moduleEntryComponent.setInput("mapObject", this.mapObject);
        this.moduleEntryComponent.setInput("selText", this.selText);
        this.moduleEntryComponent.setInput("application", this.applicationName);
        this.moduleEntryComponent.setInput("isMobileView", this.isMobileView);
        this.changeDetector.detectChanges();  //Detect changes here to ensure that ngOnInit() runs in PlokkAppBoardComponent
      }
    );
  }

  toggleSplitMap(newSplitMapState: boolean): void {
    const isMeasuring = this.displayService.getMeasureToolsVisable().getValue();
    if (isMeasuring) {
      this.displayService.flipMeasureToolsVisable();
      this.layerService.clearDrawingLayerSource(this.mapObject);
    }
    const secondaryMap = document.getElementById("secondaryMap");
    const splitMapSlider = document.getElementById("splitMapSlider");
    const splitMapControlsContainer = document.getElementById("splitMapControlsContainer");

    if (!newSplitMapState) {
      // Unregister the eventListener when split map is toggled off
      if (this.secondaryMapClickListenerKey) unByKey(this.secondaryMapClickListenerKey);
      if (secondaryMap) secondaryMap.style.display = "none";
      if (splitMapSlider) splitMapSlider.style.display = "none";
      if (splitMapControlsContainer) splitMapControlsContainer.style.display = "none";
      if (this.secondaryMapObject) this.secondaryMapObject.setTarget(undefined);

      // Add attributions back in when splitMap is toggled off
      this.displayService.setMapAttributionsActive(true);
      this.displayService.setMainActionViewActive(true);
    }
    else {
      if (secondaryMap) secondaryMap.style.display = "";
      if (splitMapSlider) splitMapSlider.style.display = "";
      if (splitMapControlsContainer) splitMapControlsContainer.style.display = "";
      if (this.secondaryMapObject) this.secondaryMapObject.setTarget("secondaryMap");
      this.displayService.setMapAttributionsActive(false);  // Remove attributions when in splitMap mode
      this.displayService.setMultipleActiveStates(false, false, false, false, false); // Close all feature views when splitMap is turned on
      // Set updates on click to get coordinates when in splitmap-mode
      this.secondaryMapClickListenerKey = this.secondaryMapObject.on(
        'click',
        (event: MapBrowserEvent<any>) => this.setNewCoordinates(event)
      );
      //We need to clear out any featureQueryTickets that might be stored in the BehaviourSubject when turning splitMap on
      this.mainActionViewService.sendNewMainActionViewItem(null)
      this.mapLayers.filter(ml => ml.layer).forEach(ml => this.applySplitMapRenderingFuncs(ml, false))
      //Initially no layer are turned on for second map, set checked status fo secondaryMaps to same as main and turn on checked layers
      if (this.secondaryMapObject.getLayers().getLength() < 1) {
        const layersToTurnOn: mapLayer[] = this.layerService.secondaryMapLayers.filter((ml: mapLayer, i: number) => this.mapLayers[i].checked && this.mapLayers[i].type !== LayerType.VEKTOR)
        this.layerService.setLayerChecked({layers: layersToTurnOn, newCheckStatus: true, isSecondaryMap: true})
      }
    }

  }

  initSecondaryMap(): void {
    this.secondaryMapObject = new Map({
      target: undefined, //Target must be null in the beginning. (The DOM element for secondaryMaps does not exist here yet)
      view: this.mapView,
    });

    const slider = <HTMLInputElement>document.getElementById("splitMapSlider");
    
    const listener = () => {
      this.mapObject.render();
      this.secondaryMapObject.render();
      const splitMapControlsMovingContainer = document.getElementById("splitMapControlsMovingContainer");
      if (splitMapControlsMovingContainer) {
        const sliderElem: HTMLInputElement = <HTMLInputElement>document?.getElementById("splitMapSlider")
        splitMapControlsMovingContainer.style.left = `${sliderElem?.value}%`;
      }
    };

    const refocus = () => {
      document.getElementById("secondaryMap")?.focus({ preventScroll: true });
    };

    slider.addEventListener("input", listener);
    slider.addEventListener("change", listener);
    slider.addEventListener("mouseup", refocus);

    mapCurExtent = this.secondaryMapObject.getView().calculateExtent(this.mapObject.getSize());

    if (this.displayService.getShowExtent()) {
      const zoomToExtent = new olZoomToExtent({ extent: mapCurExtent });
      this.secondaryMapObject.addControl(zoomToExtent)
    }
    this.secondaryMapObject.addControl(new ZoomSlider())

    if (this.applicationSettings?.option?.map?.map_tools?.fullscreen && this.mapElement) {
      this.secondaryMapObject.addControl(new FullScreen({ source: this.mapElement.nativeElement }));
    }
  }

  reloadMapview(changes: SimpleChanges): void {
    //changeArray is an array of stringified names of the Inputs that changed.
    const changeArray: string[] = Object.keys(changes);

    if (changeArray.includes("settings") && changes.settings.firstChange == false) {
      this.mapLayers = this.settings.mapLayers;

      //this.layerService.setMapLayerInfo(this.mapLayers);
      //this.layerService.setQueryLayersInfo(this.queryLayers);
      this.mapObject.render();
    }
  }

  /*
    Creates and returns a OpenLayers layer.
  */
  createOLLayer(mapLayer: mapLayer): Layer<Source, LayerRenderer<any>> | null {
    
    const layerLoaders: Record<string, (layer: mapLayer) => Layer<Source, LayerRenderer<any>> | null> = {
      WMTS: this.__load_wmts.bind(this),
      WMS: this.__load_wms.bind(this),
      WMST: this.__load_wmst.bind(this),
      WFS: this.__load_wfs.bind(this),
      OGCFeatures: this.__load_ogcFeatures.bind(this),
      XYZ: this.__load_xyz.bind(this),
      BING: this.__load_bing.bind(this),
      OSM: this.__load_osm.bind(this),
      VEKTOR: this.__load_vektor.bind(this),
      TileArcGISRest: this.__load_TileArcGISRest.bind(this),
      WEBGL: this.__load_webGL.bind(this),
      VectorTile: this.__load_vectorTile.bind(this),
      GeoJSON: this.__load_geojson.bind(this),
      CustomLayer: this.__load_custom_layer.bind(this),
      LEGEND: this.__load_legend_layer.bind(this),
      Cesium3D: this.__load_3d_layer.bind(this)
    };

    const loader = layerLoaders[mapLayer.type];
    return loader ? loader(mapLayer) : null;
  }

  /*
    Sets new coordinates on each mouse click.
    Sends the whole clickEvent(which includes the clicked location on map) down into coodinate.component where its processed and displayed.
    If coordinates property is for some reason missing, then don't send it down.
  */
  setNewCoordinates(clickOnMapEvent: MapBrowserEvent<PointerEvent>): void {
    const coordinate: any = clickOnMapEvent.coordinate;
    if (!coordinate) return;

    this.clickEventOnMapCoordinates = clickOnMapEvent;
   
    // Since we are inside of an eventListener inside the OL mapObject, we need to tell Angular manually to trigger changeDetection
    this.changeDetector.markForCheck();
  }

  /*
  __setKeyclick(): void {
   
    if (this.keyClickLatLon !== null) return; // this.keyClickLatLon was not null returning early from setKeyClick

    this.keyClickLatLon = this.mapObject.on("click", (evt: any) => {
      this.setNewCoordinates(evt)
    });
  }
  */

  __setKeyclick(): void {
    if (this.keyClickLatLon !== null) return; // this.keyClickLatLon was not null returning early from setKeyClick

    this.keyClickLatLon = this.mapObject.on("click", (evt: any) => {
      // This fixes the bug when double-clicking on some features (e.g. airQualityStations) causes 'coordinates must be finite numbers' error
      if (this.mapObject.hasFeatureAtPixel(evt.pixel)) {
        const mapElement = this.mapObject.getTargetElement();
        if (mapElement) {
          mapElement.style.pointerEvents = "none";  // Instantly ignore any rapid secondary clicks on the map viewport
          setTimeout(() => {
            mapElement.style.pointerEvents = 'auto';  // Re-enable interactions after a double-click window finishes
          }, 100);
        }
      }
      this.setNewCoordinates(evt);
    });
  }
  
  /*
    Determines what query view the feature should go down to and sends it down.
  */
  sendFeaturesToQueryView(fqvp: featureQueryViewPackage): void {
    // If there are no features in the response, then we have nothing to send down to query views so return early
    if (fqvp?.geoJSONFeature?.features?.length === 0) return;
    // Sending the feature data to the right component depending on what view it uses. If not specified send it to default show-feature-data.component
    const queryViewOfFeature: string | undefined = this.displayService.getLayerQueryView(fqvp?.metadataLayer?.id);

    switch (queryViewOfFeature) {
      case "station-view":
        this.stationDataFeature = fqvp;
        this.displayService.setShowStationDataActive(true)
        break;
      case "external-view":
        this.externalDataFeature = fqvp;
        this.displayService.setShowExternalDataActive(true)
        break;
      case "action-view":
        this.displayService.setMainActionViewActive(true)
        this.mainActionViewService.sendNewMainActionViewItem({text: fqvp?.metadataLayer?.title ?? "No metadataLayer title", type: ActionViewItemType.featureQueryResult, originalData: fqvp})
        break;
      case "table-view":
        this.featureDataFeature = fqvp;
        this.displayService.setShowFeatureDataActive(true)
        break;
      case "default":
        this.displayService.setMainActionViewActive(true)
        this.mainActionViewService.sendNewMainActionViewItem({text: fqvp?.metadataLayer?.title ?? "No metadataLayer title", type: ActionViewItemType.featureQueryResult, originalData: fqvp})
        break;
      default:
        this.displayService.setMainActionViewActive(true)
        this.mainActionViewService.sendNewMainActionViewItem({text: fqvp?.metadataLayer?.title ?? "No metadataLayer title", type: ActionViewItemType.featureQueryResult, originalData: fqvp})
        break;
    }
  }

  addScrollbuttonInteraction(): void {
    const mapBrowserCondition = (mapBrowserEvent: any) => {
      return mapBrowserEvent.originalEvent.button === 1; // 1 corresponds to the middle (scroll) mouse button
    };
    const middleMousePanInteraction = new DragPan({
      condition: mapBrowserCondition
    });
    middleMousePanInteraction.set('isScrollButtonPan', true); // Custom property to distinguish from other DragPans
    this.mapObject?.addInteraction(middleMousePanInteraction);
  }

  mapChange3(mapChangeObj: mapChange): void {
    const mapLayers: mapLayer[] = mapChangeObj.isSecondaryMap ? this.layerService.secondaryMapLayers : this.mapLayers
    const mapObj: Map = mapChangeObj.isSecondaryMap ? this.secondaryMapObject : this.mapObject

    if (mapChangeObj.newCheckStatus) {
      mapChangeObj.layers.forEach((ml: mapLayer) => {
        // If Group or Legend layer, then skip it because there is no OpenLayers layer associated with it
        if (ml.type === LayerType.LEGEND || ml.type === LayerType.GROUP) return;

        // Drag-and-drop layers already have their OL layer so we re-add them
        if (ml.layer && ml.type === LayerType.DragAndDrop) {
          ml.layer.setZIndex(1);
          mapObj.getLayers().push(ml.layer);
          return;
        }

        /*
          CustomLayers load asynchronously in __load_custom_layer().
          When async loading completes, a second newMaplayersStatus notification is fired (to notify the timeline player).
          That re-enters mapChange3 with ml.layer already set — guard against calling createOLLayer again
          which would re-trigger the async import and double-add the layer to the map.
        */
        if ((ml.type === LayerType.CustomLayer || ml.type === LayerType.Cesium3D) && ml.layer) return
        
        const newOLLayer: Layer<Source, LayerRenderer<any>> | null = this.createOLLayer(ml)

        /*
          On first load, __load_custom_layer() returns null synchronously - the actual layer is added inside its .then() callback.
          Skip the post-load setup below — it runs inside .then() instead.
        */
        if (ml.type === LayerType.CustomLayer) return
        
        if (!newOLLayer) {
          console.warn(`Could not create OL layer for: ${ml.title}/${ml.name}. Its type was: ${ml.type}`)
          return
        }
        ml.layer = newOLLayer;
        const zIndex: number = this.layerService.getZIndex(ml.id);
        if (zIndex >= 0) newOLLayer.setZIndex(zIndex);
        else newOLLayer.setZIndex(-1);

        /*
          This here is for setting it such that when in split-map mode, any opacity that was set on layers
          on the main map, then that opacity on layers in seconaryMap should be the same.
        */
        if(mapChangeObj.isSecondaryMap) {
          const opacity: number = this.mapLayers[mapLayers.findIndex((otherML: mapLayer) => otherML.id === ml.id)].layer?.getOpacity() ?? 1;
          if (opacity) ml.layer.setOpacity(opacity);
        }
        
        mapObj.getLayers().push(newOLLayer);
        
        //If basemap was changed -> update currentBasemap and check for style changes
        if (ml?.basemap) {
          this.layerService.setCurrentBasemap(ml);
          this.layerService.updateAllLegendURLs();
          this.updateLayerStyle();
        }
        //Send special ticket to mainActionView when PXWeb layer is truned on. We dont support this feature in split-map mode.
        if (ml?.option?.pxWeb && !this.displayService.splitMapActive$.value) {
          this.displayService.setMainActionViewActive(true);
          this.mainActionViewService.sendNewMainActionViewItem({"text": ml.title_lang[this.lang], type: ActionViewItemType.filter, originalData: {geoJSONFeature: null, metadataLayer: ml}});
        }

        // Apply custom rendering function to all selected layers in both maps if splitMap is active
        if (this.displayService.splitMapActive$.value) this.applySplitMapRenderingFuncs(ml, mapChangeObj.isSecondaryMap);
      })
      if (this.allowFilter) this.editFeatureService.clearFiltering();
    }
    else {
      //Remove layers from the map
      mapChangeObj.layers.forEach((ml: mapLayer) => {
        if (!ml?.layer) return
        if (ml?.type === LayerType.Cesium3D) {
          if (!this.mapObject3D) return
          const scene = this.mapObject3D.getCesiumScene();
          scene.primitives.remove(ml.layer)
        } else {
          mapObj.removeLayer(ml.layer);
        }
        
        if (ml.type !== LayerType.DragAndDrop) ml.layer = null;  // Keep the layer reference for drag-and-drop layers so features survive toggle
      })
      if (this.allowFilter) this.editFeatureService.clearFiltering();
    }
    //Once we reach here new Z indexes have been calculated for all layers and new layer created, assign to all OL layer new z-indexes
    //this.mapLayers.filter((ml: mapLayer) => ml?.checked && ml?.layer).forEach((ml: mapLayer) => {
      //ml.layer?.setZIndex(this.layerService.getZIndex(ml.id))
    //})
    //const layers = this.mapObject.getLayers().getArray();
    //layers.forEach((layer, index) => {
    //console.log(`Level ${index}:`, layer, 'Z-Index:', layer.getZIndex());
    //});
    
    // Need to wait for the object to render, otherwise it will be null in the generateLink() method
    this?.mapObject?.once('postrender', () => this.displayService.generateLink());
  }  

  applySplitMapRenderingFuncs(mapLayer: mapLayer, isSecondaryMap: boolean): void {
    if (!mapLayer?.layer) {
      console.warn("Could not find valid OL layer. Can't apply splitMap rendering functions")
      return
    }
    const [primaryPreRenderFunc, secondaryPreRenderFunc, postRenderFunc] = this.getSplitMapRenderingFuncs()

    if (isSecondaryMap) {
      mapLayer.layer.on("prerender", secondaryPreRenderFunc);
      mapLayer.layer.on("postrender", postRenderFunc);
    }
    
    if (!isSecondaryMap) {
      mapLayer.layer.on("prerender", primaryPreRenderFunc);
      mapLayer.layer.on("postrender", postRenderFunc);
    }

    this.mapObject.render()
    this.secondaryMapObject.render()
  }

  getSplitMapRenderingFuncs(): [(event: any) => void, (event: any) => void, (event: any) => void] {
    const primaryPreRenderFunc = (event: any) => {
      if (!this.displayService.splitMapActive$.value) return;
      const ctx = event.context;
      let mapSize: any = null;
      mapSize = this.secondaryMapObject.getSize();
      const slider = <HTMLInputElement>(document.getElementById("splitMapSlider"));
      const width = mapSize[0] * (parseFloat(slider.value) / 100);

      let tl = getRenderPixel(event, [0, 0]);
      let tr = getRenderPixel(event, [mapSize[0], 0]);
      let bl = getRenderPixel(event, [0, mapSize[1]]);
      let br = getRenderPixel(event, mapSize);

      if (this.displayService.splitMapActive$.value) {
        tl = getRenderPixel(event, [0, 0]);
        tr = getRenderPixel(event, [width, 0]);
        bl = getRenderPixel(event, [0, mapSize[1]]);
        br = getRenderPixel(event, [width, mapSize[1]]);
      }

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tl[0], tl[1]);
      ctx.lineTo(bl[0], bl[1]);
      ctx.lineTo(br[0], br[1]);
      ctx.lineTo(tr[0], tr[1]);
      ctx.closePath();
      ctx.clip();
    };

    const secondaryPreRenderFunc = (event: any) => {
      const ctx = event.context;
      let mapSize: any = null;
      mapSize = this.secondaryMapObject.getSize();
      const slider = <HTMLInputElement>(document.getElementById("splitMapSlider"));
      const width = mapSize[0] * (parseFloat(slider.value) / 100);
      const tl = getRenderPixel(event, [width, 0]);
      const tr = getRenderPixel(event, [mapSize[0], 0]);
      const bl = getRenderPixel(event, [width, mapSize[1]]);
      const br = getRenderPixel(event, mapSize);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tl[0], tl[1]);
      ctx.lineTo(bl[0], bl[1]);
      ctx.lineTo(br[0], br[1]);
      ctx.lineTo(tr[0], tr[1]);
      ctx.closePath();
      ctx.clip();
    };

    const postRenderFunc = (event: any) => {
      const ctx = event.context;
      ctx.restore();
    };

    return [primaryPreRenderFunc, secondaryPreRenderFunc, postRenderFunc]
  }

  /**
   * When a basemap is changed -> Checks the style of the basemap and updates others maps if necessary.
    In config for a basemap layer there might be a styleClass set: {"styleClass": "dark"} or {"styleClass": "default"}
    Then layers in Gögn also have for example in teh style section: {"default": "someStyle", "dark": "Point_mod"}
    So that layer has 2 styles, 1 meant to be used when basemap has styleClass of "default" and another style when a basemap
    that is active has a styleClass of "dark".
    This method is for updating styles of maps that are already created and checked. getLayerStyle() is for setting the correct
    style when map is checked/created.

    NOTE 05. des 2023: Refactor it such that the updating of params on layer is done in layer.service, via input of a layer and new
    params obj, wee need to also update params for user picked CQL filter in main-nav.comp
   */
  updateLayerStyle(): void {
    const currentBasemap: mapLayer | null = this.layerService.getCurrentBasemap().value
    const basemapStyleClass: string = currentBasemap?.option?.styleClass
    const checkedLayers: mapLayer[] = this.mapLayers.filter((ml: mapLayer) => ml?.checked && ml?.type !== LayerType.GROUP && ml?.type !== LayerType.VEKTOR && !ml?.basemap && !ml?.onlyquery && ml?.layer)
    //Layers that are checked and have defined a style for the current basemap styleClass
    const layersWithMultipleStyles: mapLayer[] = checkedLayers.filter((ml: mapLayer) => ml?.style && typeof ml?.style === "object")

    layersWithMultipleStyles.forEach( (ml: mapLayer) => {
      const layerStyles: any = ml.style
      const layerStyle: string = layerStyles?.[basemapStyleClass] ? layerStyles[basemapStyleClass] : (layerStyles?.["default"] ?? "")
      this.layerService.updateParamsOnLayer(ml, {"STYLES": layerStyle})
    })

    // Need to reload the WFS/WEBGL features to get the new features' style
    checkedLayers.forEach(cl => {
      if (cl.type === LayerType.WFS || cl.type === LayerType.WEBGL) { cl.layer?.changed(); }
    });
  }

  /**
   * Used in __load_* methods. Gets the right style to use on the layer.
    if its just a string, then there is only one style to use. If its an object its style is based on what styleClass the
    currently checked basemap has configured on it. Try to access the correct style defined on the gögn-layer, if it fails 
    then try to use "default" style, if that fails then use empty string.
   * @param layerStyles 
   * @returns 
   */
  getLayerStyle(layerStyles: any): string {
    const currentBasemap: mapLayer | null = this.layerService.getCurrentBasemap().value
    const basemapStyleClass: string = currentBasemap?.option?.styleClass
    return typeof layerStyles === "string" ? layerStyles : layerStyles?.[basemapStyleClass] ? layerStyles[basemapStyleClass] : (layerStyles?.["default"] ?? "")
  }

  /**
   * Reloads the maps and its layers with a new (proxy)URL, then screenshot is taken (see main-nav), then
    this function is called again to reload map and its layers with the regular URLs.
   * @returns 
   */
  reloadLayersWithNewURL(): void {
    if (this.proxyLayers.length == 0) return;
    
    this.proxyLayers.forEach((index: number) => {
      //console.log("About to reload layer below with the new URL: " + this.mapLayers[index]);
      const mapLayers: BaseLayer[] = this.mapObject.getLayers().getArray();
      const indexInMapObj: number = mapLayers.findIndex(
        (OLLayer) => OLLayer == this.mapLayers[index].layer
      );

      this.mapObject.removeLayer(this.mapLayers[index].layer as BaseLayer);
      this.mapLayers[index].layer = null;
      let layer: Layer<Source, LayerRenderer<any>> | null = null;
      layer = this.createOLLayer(this.mapLayers[index]);

      if (!layer) {
        console.warn(`Could not create OL layer for: ${this.mapLayers[index].title}/${this.mapLayers[index].name}. Its type was: ${this.mapLayers[index].type}`)
        return;
      }

      layer.setZIndex(this.layerService.getZIndex(this.mapLayers[index].id))

      this.mapLayers[index].layer = layer;

      this.mapObject
        .getLayers()
        .insertAt(indexInMapObj, this.mapLayers[index].layer as BaseLayer);
    });

    this.mapObject.render();
  }

  getMap(): Map { return this.mapObject; }
  getView(): OlView { return this.mapView; }
  getExtent(): Extent { return mapCurExtent; }

  __load_3d_layer(mapLayer: mapLayer): Layer | null {
    //const vopnURL: string = 'https://gis.natt.is/gisdata/public/vopnafjordur/tileset.json';
    (async () => {
      console.log("In async func in __load_3d_layer")
      const vopnTileset3D = await (Cesium as any).Cesium3DTileset.fromUrl(mapLayer.url);
      if (!this.mapObject3D) return null
      const scene = this.mapObject3D.getCesiumScene();
      scene.primitives.add(vopnTileset3D)
      mapLayer.layer = vopnTileset3D
    })()

    return null
  }

  __load_geojson(mapLayer: mapLayer): Layer | null {
    let layer: Layer | null = mapLayer.layer;
    if (layer !== null) return layer;

    const sourceOpt = {
      attributions: mapLayer.attributions,
      url: mapLayer.url,
      format: new GeoJSON(),
    }

    const sourceOptFinal = this.addConfigOptions(sourceOpt, mapLayer?.option?.source);
    const OLsource: VectorSource<Feature<Geometry>> = new VectorSource(sourceOptFinal);

    const layerOpt = {
      source: OLsource,
      style: this.layerService.getCustomStyle(mapLayer)
    }

    const layerOptFinal = this.addConfigOptions(layerOpt, mapLayer?.option?.layer);
    layer = new VectorLayer(layerOptFinal);
    return layer;
  }

  __load_custom_layer(mapLayer: mapLayer): Layer | null {
    /*
        Dynamically importing custom layer asynchronusly. In DB: type should be customLayer,
        name field should be the name of the file we are importing, here "cloudLayer" for example.
        The file we are importing from should have a default export that is the OL layer object.
      */ 
    import(`../../customLayers/${mapLayer.name}.ts`).then(async (loadedLayerPackage: any) => {
      //Calling .default gets the default export of the file, which is a function in this case so we execute it
      const layer: Layer = await loadedLayerPackage.default(mapLayer)
      layer.setZIndex(this.layerService.getZIndex(mapLayer.id))
      mapLayer.layer = layer
      this.mapObject.addLayer(layer)

      // Notify subscribers (e.g. timeline) now that async custom layer is fully loaded
      if (mapLayer.option?.times?.length > 0) {
        this.layerService.getNewMaplayersStatus().next({ layers: [mapLayer], newCheckStatus: true, isSecondaryMap: false });
      }
    })
    /*
      Since all other layer loading functions are synchronous we dont want to infect all of those functions
      with being asynchronous, therefore we retun null here for this. Usually you call createOLLayer() and get back the
      layer and then you set its ZIndex, add it to the map, add it to mapLayer.layer property, instead we
      do it above here and return null.
    */
    return null;
  }

  __load_legend_layer(mapLayer: mapLayer): Layer | null {
    return null;
  }

  __load_TileArcGISRest(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    let loadFunction = function (imageTile: any, src: any) { imageTile.getImage().src = src; };

    if (layer === null) {
      const initParamsConfig = { LAYERS: mapLayer.name };
      const params = this.addConfigOptions(initParamsConfig, mapLayer?.option?.params)
      let visible = true;
      if (mapLayer.onlyquery) { visible = false; }
      if (mapLayer.option !== null && "Authorization" in mapLayer.option) {
        loadFunction = (tile, src) => {
          const xhr = new XMLHttpRequest();
          xhr.responseType = "blob";
          xhr.addEventListener("loadend", function () {
            const data = this.response;
            if (data !== undefined) tile.getImage().src = URL.createObjectURL(data);
            else tile.setState(TileState.ERROR);
          });
          xhr.addEventListener("error", function () {
            tile.setState(TileState.ERROR);
          });
          xhr.open("GET", src);
          let user = undefined;
          let pass = undefined;
          if ("Authorization" in mapLayer.option) {
            user = mapLayer.option.Authorization["username"];
            pass = mapLayer.option.Authorization["password"];
          }

          if (user !== undefined && pass !== undefined) {
            xhr.setRequestHeader(
              "Authorization",
              "Basic " + btoa(user + ":" + pass)
            );
          }
          xhr.send();
        };
      }
      const sourceOpt = this.addConfigOptions(
        {
          title: mapLayer.title,
          url: mapLayer.url,
          projection: mapLayer.matrix,
          params: params,
          crossOrigin: null,
          tileLoadFunction: loadFunction,
          attributions: mapLayer.attributions,
        },
        mapLayer?.option?.source
      );

      const layerOpt = this.addConfigOptions(
        {
          source: new TileArcGISRest(sourceOpt),
          visible: visible,
        },
        mapLayer?.option?.layer
      );
      layer = new OlTileLayer(layerOpt);
    }
    return layer;
  }

  __load_wms(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    let layerOpt = {};
    let sourceOpt = {};

    if (layer === null) {
      const initParams: any = {
        VERSION: "1.3.0",
        LAYERS: mapLayer.name,
        FORMAT: mapLayer.imagetype,
      };
      if (mapLayer.style !== null) initParams["STYLES"] = this.getLayerStyle(mapLayer.style);
      const params = this.addConfigOptions(initParams, mapLayer?.option?.params)
      let visible = true;
      if (mapLayer.onlyquery) visible = false;

      //Loading layers from other providers who specify height or width
      if (mapLayer.option != null && (mapLayer.option.hasOwnProperty("maxWidth") || mapLayer.option.hasOwnProperty("maxHeight"))) {
        const loadFunction = function (imageTile: any, src: any) {
          imageTile.getImage().src = src;
        };

        sourceOpt = this.addConfigOptions(
          {
            title: mapLayer.title,
            url: mapLayer.url,
            projection: mapLayer.matrix,
            params: params,
            crossOrigin: null,
            tileLoadFunction: loadFunction,
            attributions: mapLayer.attributions,
          },
          mapLayer?.option?.source
        );

        layerOpt = this.addConfigOptions(
          {
            source: new TileWMS(),
            visible: visible,
          },
          mapLayer?.option?.layer
        );
        layer = new OlTileLayer(layerOpt);
      }
      //Load Regular layers
      else {
        const crossOriginValue = this.getCrossOriginValue(
          mapLayer.url
        );

        let loadFunction: (image: any, src: string) => void = function (image: any, src: string) {
          image.getImage().src = src;
        };

        if (mapLayer.option !== null && "Authorization" in mapLayer.option) {
          loadFunction = (imageTile: any, src: any) => {
            const xhr = new XMLHttpRequest();
            xhr.responseType = "blob";
            xhr.addEventListener("loadend", function () {
              const data = this.response;
              if (data !== undefined) imageTile.getImage().src = URL.createObjectURL(data);
              else imageTile.setState(TileState.ERROR);
            });
            xhr.addEventListener("error", function () {
              imageTile.setState(TileState.ERROR);
            });

            xhr.open("GET", src);
            const user = mapLayer.option.Authorization["username"];
            const pass = mapLayer.option.Authorization["password"];

            if (user !== undefined && pass !== undefined) {
              xhr.setRequestHeader(
                "Authorization",
                "Basic " + btoa(user + ":" + pass)
              );
            }
            xhr.send();
          };
        }

        sourceOpt = this.addConfigOptions(
          {
            title: mapLayer.title,
            url: mapLayer.url,
            projection: mapLayer.matrix,
            params: params,
            crossOrigin: crossOriginValue,
            imageLoadFunction: loadFunction,
            attributions: mapLayer.attributions,
          },
          mapLayer?.option?.source
        );

        layerOpt = this.addConfigOptions(
          {
            source: new OlSourceWMS(sourceOpt),
            visible: visible,
          },
          mapLayer?.option?.layer
        );
        layer = new OlImage(layerOpt);
      }
    }
    return layer;
  }

  __load_wmst(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    let loadFunction = function (imageTile: any, src: any) {
      imageTile.getImage().src = src;
    };

    if (layer === null) {
      const initParams: any = {
        VERSION: "1.3.0",
        LAYERS: mapLayer.name,
        'FORMAT' : mapLayer.imagetype,
        TILED: true,
      };
      if (mapLayer.style !== null) initParams["STYLES"] = this.getLayerStyle(mapLayer.style);
      const params = this.addConfigOptions(initParams, mapLayer?.option?.params)
      let visible = true;
      if (mapLayer.onlyquery) visible = false;
      if (mapLayer.option !== null && "Authorization" in mapLayer.option) {
        loadFunction = (tile, src) => {
          const xhr = new XMLHttpRequest();
          xhr.responseType = "blob";
          xhr.addEventListener("loadend", function () {
            const data = this.response;
            if (data !== undefined) tile.getImage().src = URL.createObjectURL(data);
            else tile.setState(TileState.ERROR);
          });
          xhr.addEventListener("error", function () {
            tile.setState(TileState.ERROR);
          });
          xhr.open("GET", src);
          const user = mapLayer.option.Authorization["username"];
          const pass = mapLayer.option.Authorization["password"];

          if (user !== undefined && pass !== undefined) {
            xhr.setRequestHeader(
              "Authorization",
              "Basic " + btoa(user + ":" + pass)
            );
          }
          xhr.send();
        };
      }

      const sourceOpt = this.addConfigOptions(
        {
          title: mapLayer.title,
          url: mapLayer.url,
          projection: mapLayer.matrix,
          params: params,
          crossOrigin: this.getCrossOriginValue(mapLayer.url),
          tileLoadFunction: loadFunction,
          attributions: mapLayer.attributions,
        },
        mapLayer?.option?.source
      );

      const layerOpt = this.addConfigOptions(
        {
          source: new TileWMS(sourceOpt),
          visible: visible,
        },
        mapLayer?.option?.layer
      );
      layer = new OlTileLayer(layerOpt);
    }
    return layer;
  }

  __load_xyz(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    if (layer === null) {
      let loadFunction = function (imageTile: any, src: any) {
        imageTile.getImage().src = src;
      };
      if (
        mapLayer.option !== null &&
        "Authorization" in mapLayer.option
      ) {
        loadFunction = (tile, src) => {
          const xhr = new XMLHttpRequest();
          xhr.responseType = "blob";
          xhr.addEventListener("loadend", function () {
            const data = this.response;
            if (data !== undefined) {
              tile.getImage().src = URL.createObjectURL(data);
            } else {
              tile.setState(TileState.ERROR);
            }
          });
          xhr.addEventListener("error", function () {
            tile.setState(TileState.ERROR);
          });
          xhr.open("GET", src);
          const user = mapLayer.option.Authorization["username"];
          const pass = mapLayer.option.Authorization["password"];

          if (user !== undefined && pass !== undefined) {
            xhr.setRequestHeader(
              "Authorization",
              "Basic " + btoa(user + ":" + pass)
            );
          }
          xhr.send();
        };
      }

      const initSourceConfig: any = {
        url: mapLayer.url,
        crossOrigin: "anonymous",
        projection: mapLayer.matrix,
        tileLoadFunction: loadFunction,
        attributions: mapLayer.attributions,
      }

      const sourceOpt = this.addConfigOptions(initSourceConfig, mapLayer.option?.source)
      const xyzSource = new XYZ(sourceOpt);
      const initLayerConfig = { source: xyzSource }
      const layerOpt = this.addConfigOptions(initLayerConfig, mapLayer.option?.layer)
      layer = new OlTileLayer(layerOpt);
    }
    return layer;
  }

  __load_bing(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    if (layer === null) {
      const opt = {
        key: mapLayer.matrix,
        imagerySet: mapLayer.name,
      };
      const source = new BingMaps(opt);
      layer = new OlTileLayer({
        source: source,
        visible: true,
      });
    }
    return layer;
  }

  __load_osm(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    if (layer === null) {
      const opt = mapLayer.option; //breyta
      const source = new OlOSM(opt);
      layer = new OlTileLayer({
        source: source,
        visible: true,
      });
    }
    return layer;
  }

  __load_wmts(mapLayer: mapLayer): Layer {
    let layer: Layer | null = mapLayer.layer;
    if (layer !== null) return layer;

    /*
      Some adjustement for when using proxy URL, Only related to taking a screenshot through the "PDF" button.
      Index(or key) into the "wmtsText object", the key is a URL, but when in proxy I need to make it into
      a regular URL again so that I can access the data inside when in proxy aswell.
      Note: replace function is called everytime, but only changes URL if it finds the whole proxy part.
    */
    const removeProxyURLPart: string = `${window.location.origin}/bend/pxy/`;
    let indexUrl: string = mapLayer.url.replace(removeProxyURLPart, "https://");
    let wmtsCapabilities = this.wmtsText?.[indexUrl];
    //Try with http instead of https if we got undefined back the first time with https
    if (wmtsCapabilities == undefined) {
      indexUrl = indexUrl.replace("https://", "http://");
      wmtsCapabilities = this.wmtsText?.[indexUrl];
    }

    let options: any = {};
    //getCapabilities request might have failed for this layer, therefore I check here if getCapabilities document exists. (will be null if getCapabilites request failed)
    if (this.wmtsText[indexUrl] != null) {
      options = optionsFromCapabilities(this.wmtsText[indexUrl], {
        layer: mapLayer.name,
        matrixSet: mapLayer.matrix,
      });
    }

    options["attributions"] = mapLayer.attributions;

    let loadFunction = null;

    if (mapLayer.option !== null && "Authorization" in mapLayer.option) {
      loadFunction = (tile: any, src: any) => {
        const xhr = new XMLHttpRequest();
        xhr.responseType = "blob";
        xhr.addEventListener("loadend", function () {
          const data = this.response;
          if (data !== undefined) tile.getImage().src = URL.createObjectURL(data);
          else tile.setState(TileState.ERROR);
        });
        xhr.addEventListener("error", function () {
          tile.setState(TileState.ERROR);
        });
        xhr.open("GET", src);
        const user = mapLayer.option.Authorization["username"];
        const pass = mapLayer.option.Authorization["password"];

        if (user !== undefined && pass !== undefined) {
          xhr.setRequestHeader(
            "Authorization",
            "Basic " + btoa(user + ":" + pass)
          );
        }
        xhr.send();
      };
      if (loadFunction !== null) options["tileLoadFunction"] = loadFunction;
    }
    options["crossOrigin"] = "anonymous";

    const sourceOpt = this.addConfigOptions(options, mapLayer?.option?.source);
    const source: WMTS = new WMTS(sourceOpt);
    const layerOpt = this.addConfigOptions(
      {
        source: source,
        opacity: 1,
        visible: true,
      },
      mapLayer?.option?.layer
    );
    layer = new OlTileLayer(layerOpt);
    return layer;
  }

  /**
   * Loads in a WFS layer. Fetches all feature onto this layer and loads them in on an vectorImageLayer.
   * @param mapLayer mapLayer
   * @returns void
   */
  __load_wfs(mapLayer: mapLayer): Layer {
    if(mapLayer?.option?.mp_layer?.edit?.geoEditable === true) {
      return this.__load_editable_layer(mapLayer, false);
    }
    let layer: Layer | null = mapLayer.layer;
    
    let source: VectorSource;
    if (layer === null) {
      const sourceOpt = this.addConfigOptions({attributions: mapLayer.attributions}, mapLayer?.option?.source);
      source = new VectorSource(sourceOpt);

      layer = new VectorImageLayer({
        source: source,
        style: this.layerService.getCustomStyle(mapLayer)
      });
      
      //https://gis.natt.is/geoserver/wms?SERVICE=WFS&version=2.0.0&request=GetFeature&srsName=EPSG:3057&outputFormat=application/json&typeNames=Hagstofan:smasvaedi_2021
      //Note, when this layer is loaded, this.mapObject?.getView()?.getProjection()?.getCode() doesnt have the correct EPSG, global mapProjection hovewer does have the application EPSG as defined in config,
      const getAllFeatureWFSRequest: string = `${mapLayer.url}?SERVICE=WFS&version=2.0.0&request=GetFeature&srsName=${mapProjection ?? this.mapObject?.getView()?.getProjection()?.getCode() ?? "EPSG:3057"}&outputFormat=application/json&typeNames=${mapLayer.name}`

      this.http.get(getAllFeatureWFSRequest).subscribe(
        (data: GeoJSONFeatureCollection | object) => {
          const features: Feature<Geometry>[] = new GeoJSON().readFeatures(data)
          source.addFeatures(features)
          //Since we are using addFeatures, we need to manually call this event if we want to listen to it elsewhere
          layer?.getSource()?.dispatchEvent("featuresloadend")
        }
      )      
    }
    return layer;
  }

  /**
   * Loads in a OGC layer. Fetches all feature onto this layer and loads them in on an vectorImageLayer.
   * @param mapLayer 
   * @returns 
   */
  __load_ogcFeatures(mapLayer: mapLayer): Layer {
    let source: VectorSource;
    const sourceOptions = this.addConfigOptions({}, mapLayer?.option?.source);
    source = new VectorSource(sourceOptions);

    const layer = new VectorImageLayer({
      source: source,
      style: this.layerService.getCustomStyle(mapLayer)
    });

    const viewProjection = this.mapObject?.getView()?.getProjection()?.getCode() || 'EPSG:3857';
    const requestProjection = mapProjection || viewProjection;

    const requestURL: string = `${mapLayer.url}?crs=${mapProjection ?? this.mapObject?.getView()?.getProjection()?.getCode() ?? "EPSG:3857"}`;

    this.http.get(requestURL).subscribe(
      (data: GeoJSONFeatureCollection | object) => {
        const features: Feature<Geometry>[] = new GeoJSON().readFeatures(data, {
          dataProjection: requestProjection,
          featureProjection: viewProjection
        })
        source.addFeatures(features)
      },
      (error) => console.error(`Error loading OGC features for ${mapLayer.name}:`, error)
    );
    return layer;
  }

  /**
  * Loads in a WebGL layer. Fetches all feature onto this layer and loads them in on a WebGL vector layer.
  * @param mapLayer mapLayer
  * @returns void
  */
  __load_webGL(mapLayer: mapLayer): Layer {
    if (mapLayer?.option?.mp_layer?.edit?.geoEditable) { return this.__load_editable_layer(mapLayer, true); }

    let layer: Layer | null = mapLayer.layer;

    if (layer === null) {
      const sourceOpt = this.addConfigOptions({attributions: mapLayer.attributions}, mapLayer?.option?.source);
      const source = new VectorSource(sourceOpt);

      layer = new WebGLVectorLayer({
        source: source,
        style: this.layerService.getCustomStyle(mapLayer)
      });

      const getAllFeatureWFSRequest: string = `${mapLayer.url}?SERVICE=WFS&version=2.0.0&request=GetFeature&srsName=${mapProjection ?? this.mapObject?.getView()?.getProjection()?.getCode() ?? "EPSG:3057"}&outputFormat=application/json&typeNames=${mapLayer.name}`

      this.http.get(getAllFeatureWFSRequest).subscribe(
        (data: GeoJSONFeatureCollection | object) => {
          const features: Feature<Geometry>[] = new GeoJSON().readFeatures(data)
          source.addFeatures(features)
        }
      )
    }
    return layer;
  }

  /**
   * Loads in a VectorTile layer
   * Currently only used for loading in Mapbox Vector Tiles (MVT)
   * MVT is a specification and format for encoding geospatial data into compact vector tiles making it efficient for web and mobile applications
   * It organizes data into layers and features (points, lines, polygons) within a tile and uses Protocol Buffers (PBF) for serialization, which allows for fast loading and dynamic styling
   * @param mapLayer 
   * @returns 
   */
  __load_vectorTile(mapLayer: mapLayer): VectorTileLayer {
    const source = new VectorTileSource({
      format: new MVT(),  // The underlying vector tile protocol (PBF binary tiles) is encoded as Mapbox Vector Tiles (MVT)
      url: mapLayer.url,
      attributions: mapLayer.attributions,
      projection: mapLayer.matrix ?? mapProjection,
    });

    const layer = new VectorTileLayer({
      source,
      style: this.layerService.getCustomStyle(mapLayer),
    });
    
    return layer;
  }

  /**
   * Sets up a watcher on the map's zoom level to manage the visibility of feature vertices
   * Otherwise, vertices may clutter the map at lower zoom levels
   */
  private setupZoomWatcher(): void {
    const view = this.mapObject.getView();
    const loadFeaturesZoom = this.applicationSettings.option?.edit?.loadFeaturesZoom ?? 0;

    view.on('change:resolution', () => {
      const zoom = view.getZoom();
      if (zoom === undefined) return;
      if (!this.displayService.getEditFeatureActiveInActionView().getValue()) return;

      const selectedFeature = this.editFeatureService.selectInteraction?.getFeatures()?.item(0) ?? null;

      if (zoom < loadFeaturesZoom) this.editFeatureService.hideVertices();
      else if (selectedFeature) this.editFeatureService.showVertices(selectedFeature);
    });
  }

  private __load_editable_layer(mapLayer: mapLayer, isWebGL = false): Layer {
    const vectorSource = this.layerService.getVectorSource(mapLayer, this.applicationSettings);
    const loadFeaturesZoom = this.applicationSettings.option?.edit?.loadFeaturesZoom ?? 1;

    const layer = isWebGL
      ? this.layerService.getWebGLVectorLayer(mapLayer, vectorSource, loadFeaturesZoom)
      : this.layerService.getVectorImageLayer(mapLayer, vectorSource, loadFeaturesZoom);

    this.editFeatureService.selectedInteractionLayers.push(layer);

    const actionKey = this.applicationSettings?.option?.edit?.actionColumn;

    vectorSource.on('featuresloadend', (ev) => {
      if (ev.features) {
        const isFiltering = this.editFeatureService.isFilterTextBoxVisible && this.filterColumn;

        ev.features.forEach((feature: any) => {
          const props = feature.getProperties();
          const filterValue = props[this.filterColumn];
          const actionValue = props[actionKey];

          // TODO: features with actionValue = 'delete' should rather be removed (not selected) in the backend process
          if ((isFiltering && filterValue !== this.editFeatureService.filterText) || actionValue === "delete") {
            vectorSource.removeFeature(feature);
          }
        });

        let layerFilter = this.layerService.wfsLayersFilters.find(filter => filter.layerToUpdate.id === mapLayer.id);

        if (!layerFilter) {
          layerFilter = {
            features: [],
            layerToUpdate: mapLayer,
            updatedParams: {} as LayerParams
          };
          this.layerService.wfsLayersFilters.push(layerFilter);
        }

        layerFilter.features.push(...ev.features);

        if (layerFilter.layerToUpdate && layerFilter.updatedParams.cql_filter && mapLayer.id === layerFilter.layerToUpdate.id) {
          this.layerService.updateParamsOnEditableLayer(layerFilter.layerToUpdate, layerFilter.updatedParams);
        }
      }
    });
    return layer;
  }

  __load_vektor(mapLayer: mapLayer): Layer {
    let layer: VectorLayer | null = mapLayer.layer as VectorLayer;
    if (layer === null) {
      const source = new VectorSource({
        attributions: mapLayer.attributions,
        format: new GeoJSON({
          dataProjection: mapProjection,
        }),
      });
      layer = new VectorLayer({
        source: source,
        style: this.layerService.getDefaultVectorDrawStyle(),
        visible: true,
      });
    }

    this.layerService.OLvLayer = layer
    return layer;
  }

  getCrossOriginValue(url: string): string {
    let cr: any = null; // cr (crossOrigin) null if coming from other sources than ourselves here at LMI
    /*
      Realized that it must be null for most layers from others than LMI. It can only be "anonymous" if the server has
      the response header: "Access-Control-Allow-Origin: *"
    */
    const lmiRegex: RegExp = /gis.lmi/;
    if (lmiRegex.test(url)) cr = "anonymous";
    return cr;
  }

  // Receives events from external-view.component to notify map.component to destroy it
  destroyExternalDataView(): void {
    this.displayService.setShowExternalDataActive(false);
  }

  destroyFeatureDataView(): void {
    this.displayService.setShowFeatureDataActive(false);
  }

  destroyStationDataView(): void {
    this.displayService.setShowStationDataActive(false);
  }

  /*  Note 29.11.2023
      Change for the cql filter definition for getMap request. Before it was:
      "source": {
        "cql_filter": "teg_rannsoknar = 'björgunarrannsókn'"
      }

      now its :
      "params": {
        "cql_filter": "teg_rannsoknar = 'björgunarrannsókn'"
      }

      This is to make it more general and in sync with "source" and "layer" OL options, 
      maybe i future we also want other custom params defined on the layer.
  */

  // Takes in 2 objects and combines them
  addConfigOptions(initialConfig: any, additionalConfig: any): any {
    if (!additionalConfig) {
      //console.warn("additionalConfig was not valid, can't add it. AdditionalConfig: " + additionalConfig).
      return initialConfig;
    }

    //Special case for tileGrid config. Second check is so we dont recreate the TileGrid
    if (additionalConfig?.tileGrid && !(additionalConfig?.tileGrid instanceof TileGrid)) {
      additionalConfig.tileGrid = new TileGrid(additionalConfig.tileGrid);
    }

    const combinedConfig: any = {...initialConfig, ...additionalConfig};
    return combinedConfig;
  }


  /**
   * This is necessary for iOS, because it doesn’t support the standard fullscreen API,
    so we simulate fullscreen by manually applying a CSS class that makes the element fill the screen
   */
  toggleFullscreenForIos(): void {
    const mapEl = document.getElementById('mapid');   // The map element
    if (mapEl) {
      this.isFullscreen = !this.isFullscreen;

      if (this.isFullscreen) {
        // Enter iOS fullscreen mode
        mapEl.classList.add('fullscreen-ios');
        document.addEventListener('keydown', this.handleKeyDown);  // Add ESC listener
        if (!this.isMobileView) {
          // Only send this if user is not on mobile device, to avoid sending message about pressing button (ESC) when device has no keyboard
          this.displayService.toastrShowInfo({
            message: this.selText.exitFullscreen,
            positionClass: ToastPosition.TopCenter,
            tapToDismiss: true,
            timeOut: 3000
          });
        }
      }
      else this.exitFullscreenForIos();
    }
  }

  /**
   * Allow ESC to exit fullscreen mode on iOS devices
   */
  exitFullscreenForIos(): void {
    const mapEl = document.getElementById('mapid');
    if (mapEl) {
      this.isFullscreen = false;
      mapEl.classList.remove('fullscreen-ios');
      document.removeEventListener('keydown', this.handleKeyDown);  // Remove ESC listener
      this.changeDetector.detectChanges();
    }
  }

  /**
   * Cleanup the attributions string by removing trailing commas and whitespace
   * @param attr 
   * @returns 
   */
  private sanitizeAttribution(attr: string): string {
    if (!attr) return "";

    return attr
      .replace(/^[\s,]+/, "")   // Remove commas/spaces from the START
      .replace(/[\s,]+$/, "")   // Remove commas/spaces from the END
      .trim();                  // Final cleanup of any whitespace
  }
}